import React, { useState, useEffect, useRef } from 'react';
import { SummaryRenderer } from './SummaryRenderer';

const RobotAvatar = ({ size = 44 }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="40" cy="40" r="40" fill="url(#bot-gradient-ai)" />
    <rect x="22" y="24" width="36" height="26" rx="8" fill="white" />
    <circle cx="32" cy="35" r="4" fill="#0D47A1" />
    <circle cx="48" cy="35" r="4" fill="#0D47A1" />
    <path d="M32 43 C 36 47, 44 47, 48 43" stroke="#0D47A1" strokeWidth="2.5" strokeLinecap="round" fill="none" />
    <circle cx="40" cy="16" r="3" fill="white" />
    <line x1="40" y1="19" x2="40" y2="24" stroke="white" strokeWidth="2" />
    <rect x="18" y="32" width="4" height="10" rx="2" fill="white" />
    <rect x="58" y="32" width="4" height="10" rx="2" fill="white" />
    <path d="M26 56 C 26 50, 54 50, 54 56 L50 66 H30 Z" fill="white" />
    <defs>
      <linearGradient id="bot-gradient-ai" x1="0" y1="0" x2="80" y2="80" gradientUnits="userSpaceOnUse">
        <stop stopColor="#0B3C95" />
        <stop offset="1" stopColor="#1E65FF" />
      </linearGradient>
    </defs>
  </svg>
);

function AITab({ user }) {
  const [view, setView] = useState('assistant');
  const [showHistory, setShowHistory] = useState(false);
  const [conversations, setConversations] = useState(() => {
    try {
      const saved = localStorage.getItem(`conversations_${user?.id}`);
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [currentConvId, setCurrentConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState('');
  const [patterns, setPatterns] = useState('');
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [timePeriod, setTimePeriod] = useState('all');
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  const saveConversation = (msgs) => {
    if (msgs.length === 0) return;
    const convId = currentConvId || Date.now().toString();
    const firstMsg = msgs.find(m => m.role === 'user')?.text || 'New conversation';
    const title = firstMsg.length > 40 ? firstMsg.slice(0, 40) + '...' : firstMsg;
    const conv = { id: convId, title, messages: msgs, updatedAt: new Date().toISOString() };
    setCurrentConvId(convId);
    const updated = [conv, ...conversations.filter(c => c.id !== convId)].slice(0, 20);
    setConversations(updated);
    localStorage.setItem(`conversations_${user?.id}`, JSON.stringify(updated));
  };

  const loadConversation = (conv) => {
    setCurrentConvId(conv.id);
    setMessages(conv.messages);
    setShowHistory(false);
  };

  const startNewChat = () => {
    setCurrentConvId(null);
    setMessages([]);
    setShowHistory(false);
  };

  const deleteConversation = (convId, e) => {
    e.stopPropagation();
    const updated = conversations.filter(c => c.id !== convId);
    setConversations(updated);
    localStorage.setItem(`conversations_${user?.id}`, JSON.stringify(updated));
    if (currentConvId === convId) startNewChat();
  };

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text) => {
    const msg = text || input.trim();
    if (!msg) return;
    const newMessages = [...messages, { role: 'user', text: msg, time: new Date().toISOString() }];
    setMessages(newMessages);
    setInput('');
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/AI/chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patientId: user.id, message: msg })
      });
      const data = await res.json();
      const reply = data.response || 'Sorry, could not process that.';
      const updatedMessages = [...newMessages, { role: 'assistant', text: reply, time: new Date().toISOString() }];
      setMessages(updatedMessages);
      saveConversation(updatedMessages);
    } catch {
      const updatedMessages = [...newMessages, { role: 'assistant', text: 'Cannot connect to AI.', time: new Date().toISOString() }];
      setMessages(updatedMessages);
    }
    setLoading(false);
  };

  const clearHistory = () => {
    setMessages([]);
    localStorage.removeItem(`chat_history_${user?.id}`);
  };

  const formatMessage = (text) => {
    if (!text) return null;
    const lines = text.split('\n');
    return lines.map((line, i) => {
      if (!line.trim()) return <div key={i} className="h-1.5" />;
      if (/^[📅🟢⚠️💊📈🚨✅🔴📊🩺💡🏥🔬💉🧪]/.test(line.trim())) {
        return (
          <div key={i} className="flex items-center gap-2 mt-3 mb-1.5 pb-1 border-b border-blue-100">
            <p className="font-black text-[#0B3C95] text-xs">{line}</p>
          </div>
        );
      }
      if (line.startsWith('### ')) return <p key={i} className="font-black text-[#0B3C95] text-sm mt-2 mb-1">{line.replace(/^###\s/, '')}</p>;
      if (line.startsWith('## ')) return <p key={i} className="font-black text-[#0B3C95] text-sm mt-2 mb-1">{line.replace(/^##\s/, '')}</p>;
      if (line.startsWith('# ')) return <p key={i} className="font-black text-[#0B3C95] text-sm mt-2 mb-1">{line.replace(/^#\s/, '')}</p>;
      if (/^[-•*]\s/.test(line.trim()) || /^\d+\.\s/.test(line.trim())) {
        const content = line.replace(/^[-•*]\s/, '').replace(/^\d+\.\s/, '');
        return (
          <div key={i} className="flex items-start gap-2 mb-1.5">
            <div className="w-1.5 h-1.5 bg-[#0B3C95] rounded-full mt-1.5 shrink-0"></div>
            <p className="text-xs text-[#0D1B2A] leading-relaxed flex-1">{renderInline(content)}</p>
          </div>
        );
      }
      return <p key={i} className="text-xs text-[#0D1B2A] leading-relaxed mb-1">{renderInline(line)}</p>;
    });
  };

  const renderInline = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) return <strong key={i} className="font-black text-[#0B3C95]">{part.slice(2, -2)}</strong>;
      if (part.startsWith('*') && part.endsWith('*')) return <em key={i} className="italic text-gray-600">{part.slice(1, -1)}</em>;
      if (part.startsWith('`') && part.endsWith('`')) return <code key={i} className="bg-blue-50 text-[#0B3C95] px-1 py-0.5 rounded text-[10px] font-mono">{part.slice(1, -1)}</code>;
      return part;
    });
  };

  const getSummary = async (period = timePeriod) => {
    setSummaryLoading(true); setSummary('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/AI/summary/${user.id}?period=${period}`);
      const data = await res.json();
      setSummary(data.summary || 'No summary available.');
    } catch { setSummary('Cannot connect to server.'); }
    setSummaryLoading(false);
  };

  const getPatterns = async (period = timePeriod) => {
    setSummaryLoading(true); setPatterns('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/AI/patterns/${user.id}?period=${period}`);
      const data = await res.json();
      setPatterns(data.patterns || 'No patterns detected.');
    } catch { setPatterns('Cannot connect to server.'); }
    setSummaryLoading(false);
  };

  const downloadSummary = () => {
    const date = new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
    const content = `MYHEALTH JOURNAL — AI HEALTH SUMMARY\n=====================================\nPatient: ${user?.name}\nPatient ID: ${user?.patientUid || 'N/A'}\nGenerated On: ${date}\n\nOVERALL HEALTH SUMMARY\n----------------------\n${summary || 'No summary generated yet.'}\n\nHEALTH PATTERNS\n---------------\n${patterns || 'No patterns detected yet.'}\n\n=====================================\nThis summary was generated by MyHealth Journal AI.\nFor medical advice, please consult your doctor.`.trim();
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `MyHealth_Summary_${user?.name?.replace(/ /g, '_')}_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const shareSummary = async () => {
    const date = new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
    const text = `MyHealth Journal — AI Summary for ${user?.name}\nGenerated: ${date}\n\n${summary}\n\n${patterns}`;
    if (navigator.share) {
      await navigator.share({ title: 'My Health Summary', text });
    } else {
      await navigator.clipboard.writeText(text);
      alert('Summary copied to clipboard!');
    }
  };

  const suggestedQuestions = [
    { icon: 'clinical_notes', color: '#2f5fff', bg: '#e6ecff', text: 'Summarize all my reports' },
    { icon: 'search', color: '#0aa1c0', bg: '#e0f4f8', text: 'Find my blood test report' },
    { icon: 'pill', color: '#e11d74', bg: '#fbe1ea', text: 'What medicines am I taking?' },
    { icon: 'warning', color: '#e08a1f', bg: '#fdf1de', text: 'Any concerns in my reports?' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#F4F7FC', fontFamily: 'Inter, sans-serif', overflow: 'hidden' }}>

      {/* TAB TOGGLE */}
      <div style={{ padding: '12px 16px 8px', background: '#F4F7FC', flexShrink: 0 }}>
        <div className="flex gap-1.5 items-center">
          <button onClick={() => setShowHistory(!showHistory)}
            className={`w-10 h-10 rounded-2xl flex items-center justify-center border shadow-sm transition-all active:scale-95 relative shrink-0 ${showHistory ? 'bg-[#0B3C95] border-[#0B3C95]' : 'bg-white border-gray-100'}`}>
            <span className="material-symbols-outlined" style={{ fontSize: '18px', color: showHistory ? 'white' : '#6b7280', fontVariationSettings: '"FILL" 1' }}>menu</span>
            {conversations.length > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#0B3C95] text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                {conversations.length}
              </span>
            )}
          </button>
          {[
            { id: 'assistant', icon: 'smart_toy', label: 'AI Assistant' },
            { id: 'summary', icon: 'description', label: 'AI Summary' },
          ].map(tab => (
            <button key={tab.id} onClick={() => setView(tab.id)}
              className={`flex-1 min-w-0 flex items-center justify-center gap-1 px-2 py-2.5 rounded-full text-xs font-bold whitespace-nowrap transition-all shadow-sm active:scale-95 ${view === tab.id ? 'bg-[#0B3C95] text-white' : 'bg-white text-gray-500 border border-gray-100'}`}>
              <span className="material-symbols-outlined shrink-0" style={{ fontSize: '16px', color: view === tab.id ? 'white' : '#0B3C95', fontVariationSettings: view === tab.id ? '"FILL" 1' : '"FILL" 0' }}>{tab.icon}</span>
              <span className="truncate">{tab.label}</span>
            </button>
          ))}
          <button onClick={startNewChat}
            className="w-10 h-10 rounded-2xl flex items-center justify-center border border-gray-100 bg-white shadow-sm active:scale-95 shrink-0">
            <span className="material-symbols-outlined text-[#0B3C95]" style={{ fontSize: '18px' }}>edit_square</span>
          </button>
        </div>
      </div>

      {/* HISTORY SIDEBAR */}
      {showHistory && (
        <div className="fixed inset-0 z-[150] flex" style={{ fontFamily: 'Inter, sans-serif' }}>
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowHistory(false)}></div>
          <div className="relative w-72 bg-white h-full shadow-2xl flex flex-col">
            <div className="px-4 py-4 border-b border-gray-100" style={{ background: 'linear-gradient(135deg, #0B3C95, #1E65FF)' }}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-black text-white text-base">Chat History</p>
                  <p className="text-white/70 text-xs mt-0.5">{conversations.length} conversations</p>
                </div>
                <button onClick={() => setShowHistory(false)} className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center active:scale-95">
                  <span className="material-symbols-outlined text-white" style={{ fontSize: '18px' }}>close</span>
                </button>
              </div>
              <button onClick={startNewChat} className="mt-3 w-full py-2.5 bg-white/20 text-white font-bold rounded-xl text-xs flex items-center justify-center gap-2 active:scale-95 border border-white/30">
                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>add</span>New Chat
              </button>
            </div>
            <div className="flex-1 overflow-y-auto py-2">
              {conversations.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-center px-4">
                  <span className="material-symbols-outlined text-gray-200 mb-2" style={{ fontSize: '36px' }}>chat_bubble</span>
                  <p className="text-xs text-gray-400">No past conversations yet</p>
                </div>
              ) : (
                conversations.map(conv => (
                  <div key={conv.id} onClick={() => loadConversation(conv)}
                    className={`w-full flex items-start gap-3 px-4 py-3 hover:bg-gray-50 active:bg-gray-100 text-left border-b border-gray-50 transition-all cursor-pointer ${currentConvId === conv.id ? 'bg-[#EAF2FF] border-l-4 border-l-[#0B3C95]' : ''}`}>
                    <span className="material-symbols-outlined text-gray-400 mt-0.5 shrink-0" style={{ fontSize: '18px', fontVariationSettings: '"FILL" 1' }}>chat</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#0D1B2A] truncate">{conv.title}</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">{conv.messages.length} messages • {new Date(conv.updatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</p>
                    </div>
                    <button onClick={(e) => deleteConversation(conv.id, e)} className="w-6 h-6 flex items-center justify-center text-gray-300 hover:text-red-400 rounded-full shrink-0 mt-0.5">
                      <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>delete</span>
                    </button>
                  </div>
                ))
              )}
            </div>
            {conversations.length > 0 && (
              <div className="px-4 py-3 border-t border-gray-100">
                <button onClick={() => { setConversations([]); localStorage.removeItem(`conversations_${user?.id}`); startNewChat(); }}
                  className="w-full py-2 text-red-500 font-bold text-xs border border-red-200 rounded-xl active:scale-95">
                  🗑️ Clear All History
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ✅ FIXED ASSISTANT VIEW — input bar always stays at bottom */}
      {view === 'assistant' && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
          {/* ✅ Chat scroll area — leaves space for input bar */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px 8px' }}>
            <div className="flex items-start gap-4 mb-5 p-4 rounded-3xl border" style={{ background: '#e8eaf9', borderColor: '#dbdff2' }}>
              <div className="w-14 h-14 rounded-full bg-white shrink-0 flex items-center justify-center shadow-sm">
                <RobotAvatar size={36} />
              </div>
              <div className="flex-1 pt-1">
                <p className="font-black text-[#0D1B2A] text-lg mb-1">MedAI Assistant</p>
                <p className="text-sm text-gray-500 leading-snug">Hello {user?.name?.split(' ')[0]}! 👋<br />How can I help you today?</p>
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-green-200 rounded-full">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-green-600 text-xs font-bold">Online</span>
                </div>
                {messages.length > 0 && (
                  <button onClick={clearHistory} className="w-8 h-8 bg-white rounded-full flex items-center justify-center active:scale-95 shadow-sm">
                    <span className="material-symbols-outlined text-gray-400" style={{ fontSize: '15px' }}>delete</span>
                  </button>
                )}
              </div>
            </div>

            {messages.length === 0 && (
              <div className="mb-4">
                <p className="text-base font-bold text-[#191c1e] mb-3 px-1">Suggested questions</p>
                <div className="flex flex-col gap-2.5">
                  {suggestedQuestions.map((q, i) => (
                    <button key={i} onClick={() => sendMessage(q.text)}
                      className="flex items-center gap-3 bg-white border border-gray-100 rounded-2xl p-3.5 text-left active:scale-95 transition-all shadow-sm">
                      <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: q.bg }}>
                        <span className="material-symbols-outlined" style={{ fontSize: '20px', color: q.color }}>{q.icon}</span>
                      </div>
                      <p className="text-sm font-bold text-[#191c1e] leading-snug flex-1">{q.text}</p>
                      <span className="material-symbols-outlined text-gray-300 shrink-0" style={{ fontSize: '20px' }}>chevron_right</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-4">
              {messages.map((msg, i) => (
                <div key={i} className={`flex items-end gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.role === 'assistant' && (
                    <div className="w-7 h-7 rounded-full shrink-0 flex items-center justify-center mb-0.5" style={{ background: 'linear-gradient(135deg, #0B3C95, #1E65FF)' }}>
                      <RobotAvatar size={18} />
                    </div>
                  )}
                  <div className={`max-w-[82%] rounded-2xl shadow-sm ${msg.role === 'user' ? 'bg-[#0B3C95] text-white rounded-br-sm px-4 py-3' : 'bg-white border border-gray-100 rounded-bl-sm px-4 py-3'}`}>
                    {msg.role === 'user' ? (
                      <p className="text-sm leading-relaxed">{msg.text}</p>
                    ) : (
                      <div>
                        {formatMessage(msg.text)}
                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                          <p className="text-[10px] text-amber-600">⚠️ Not medical advice</p>
                          {msg.time && <p className="text-[10px] text-gray-300">{new Date(msg.time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</p>}
                        </div>
                      </div>
                    )}
                  </div>
                  {msg.role === 'user' && (
                    <div className="w-7 h-7 rounded-full shrink-0 bg-[#0B3C95]/10 flex items-center justify-center mb-0.5 text-[#0B3C95] font-black text-xs">
                      {user?.name?.charAt(0) || 'U'}
                    </div>
                  )}
                </div>
              ))}
              {loading && (
                <div className="flex items-end gap-2 justify-start">
                  <div className="w-7 h-7 rounded-full shrink-0 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0B3C95, #1E65FF)' }}>
                    <RobotAvatar size={18} />
                  </div>
                  <div className="bg-white border border-gray-100 px-4 py-3 rounded-2xl rounded-bl-sm shadow-sm">
                    <div className="flex gap-1 items-center">
                      {[0, 0.15, 0.3].map((d, i) => (
                        <div key={i} className="w-2 h-2 bg-[#0B3C95] rounded-full animate-bounce" style={{ animationDelay: `${d}s` }} />
                      ))}
                    </div>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
          </div>

          {/* ✅ INPUT BAR — always at bottom, never moves */}
          <div style={{ flexShrink: 0, padding: '12px 16px', background: '#F4F7FC', borderTop: '1px solid #efefef' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#fff', borderRadius: '50px', padding: '10px 16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0' }}>
              <span className="material-symbols-outlined" style={{ fontSize: '20px', color: '#6d5ce8', flexShrink: 0 }}>auto_awesome</span>
              <input
                ref={inputRef}
                type="text"
                placeholder="Ask about your health..."
                style={{ flex: 1, background: 'transparent', border: 'none', fontSize: '16px', outline: 'none', color: '#0D1B2A', minWidth: 0 }}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && sendMessage()}
              />
              <button
                onClick={() => sendMessage()}
                disabled={!input.trim()}
                style={{ width: '44px', height: '44px', borderRadius: '50%', background: '#3446e0', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, opacity: input.trim() ? 1 : 0.4, cursor: 'pointer' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '20px', color: '#fff' }}>send</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SUMMARY VIEW */}
      {view === 'summary' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }} className="space-y-3">
          <div>
            <h2 className="font-black text-[#191c1e] text-xl flex items-center gap-2">
              AI Health Summary
              <span className="material-symbols-outlined shrink-0" style={{ fontSize: '18px', color: '#3446e0' }}>verified_user</span>
            </h2>
            <div className="flex items-center justify-between gap-2 mt-1">
              <p className="text-sm text-gray-400">Powered by <span className="font-semibold" style={{ color: '#6d5ce8' }}>MyHealth Journal AI</span></p>
              <button onClick={() => { getSummary(); getPatterns(); }} disabled={summaryLoading}
                className="flex items-center gap-1.5 px-3.5 py-2 bg-gray-100 text-[#191c1e] font-bold rounded-full text-xs active:scale-95 disabled:opacity-50 shrink-0">
                <span className="material-symbols-outlined" style={{ fontSize: '15px', color: '#3446e0' }}>refresh</span>
                {summaryLoading ? 'Analyzing...' : 'Refresh'}
              </button>
            </div>
          </div>

          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Time Period</p>
            <div className="flex gap-2 mb-2">
              {[{ id: 'all', label: 'All Time' }, { id: '1year', label: '1 Year' }, { id: '6months', label: '6 Months' }, { id: '3months', label: '3 Months' }].map(p => (
                <button key={p.id} onClick={() => { setTimePeriod(p.id); setSummary(''); setPatterns(''); }}
                  className={`flex-1 py-2.5 rounded-full text-xs font-bold transition-all active:scale-95 ${timePeriod === p.id ? 'bg-[#0d2f8f] text-white shadow-md' : 'bg-white text-[#191c1e] border border-gray-200'}`}>
                  {p.label}
                </button>
              ))}
            </div>
            <div className="flex gap-2 items-center">
              <div className="flex-1 min-w-0 flex items-center bg-white rounded-2xl border border-gray-200 px-4 py-3 gap-2">
                <span className="material-symbols-outlined text-gray-400 shrink-0" style={{ fontSize: '18px' }}>calendar_month</span>
                <input type="text" placeholder="Custom period (e.g. 2 years)"
                  className="flex-1 min-w-0 text-sm bg-transparent outline-none text-[#0D1B2A] placeholder-gray-400"
                  style={{ textOverflow: 'ellipsis' }}
                  onKeyDown={e => { if (e.key === 'Enter' && e.target.value.trim()) { setTimePeriod('custom:' + e.target.value.trim()); setSummary(''); setPatterns(''); } }} />
              </div>
              <button onClick={(e) => {
                const inp = e.target.closest('div').previousSibling?.querySelector('input');
                if (inp?.value.trim()) { setTimePeriod('custom:' + inp.value.trim()); setSummary(''); setPatterns(''); }
              }} className="px-4 py-3 bg-[#0d2f8f] text-white font-bold rounded-full text-sm active:scale-95 shrink-0">Set</button>
            </div>
            {timePeriod.startsWith('custom:') && (
              <p className="text-xs text-[#0B3C95] font-semibold mt-1 ml-1">✅ Custom period: {timePeriod.replace('custom:', '')}</p>
            )}
          </div>

          <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: '#ede9fb' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', color: '#6d5ce8' }}>description</span>
                </div>
                <p className="font-black text-[#191c1e] text-lg">Overall Health Summary</p>
              </div>
              {summary && (
                <div className="flex gap-1.5 shrink-0">
                  <button onClick={downloadSummary} className="flex items-center gap-1 px-2 py-1 bg-green-50 text-green-600 font-bold rounded-lg text-xs active:scale-95">
                    <span className="material-symbols-outlined" style={{ fontSize: '13px' }}>download</span>Save
                  </button>
                  <button onClick={shareSummary} className="flex items-center gap-1 px-2 py-1 bg-[#0B3C95]/10 text-[#0B3C95] font-bold rounded-lg text-xs active:scale-95">
                    <span className="material-symbols-outlined" style={{ fontSize: '13px' }}>share</span>Share
                  </button>
                </div>
              )}
            </div>
            {summary ? <SummaryRenderer text={summary} /> : (
              <button onClick={() => getSummary(timePeriod)} disabled={summaryLoading}
                className="w-full py-4 bg-[#0d2f8f] text-white font-bold rounded-2xl text-base flex items-center justify-center gap-2 disabled:opacity-50 active:scale-95">
                <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>auto_awesome</span>
                {summaryLoading ? 'Analyzing your records...' : `Generate ${timePeriod === 'all' ? 'All Time' : timePeriod === '1year' ? '1 Year' : timePeriod === '6months' ? '6 Month' : timePeriod === '3months' ? '3 Month' : timePeriod.startsWith('custom:') ? timePeriod.replace('custom:', '') : timePeriod} Summary`}
              </button>
            )}
          </div>

          <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: '#ede9fb' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', color: '#6d5ce8' }}>trending_up</span>
                </div>
                <p className="font-black text-[#191c1e] text-lg">Health Patterns</p>
              </div>
              <button onClick={getPatterns} disabled={summaryLoading}
                className="flex items-center gap-1 text-sm font-bold px-3 py-2 rounded-full active:scale-95 disabled:opacity-50 shrink-0" style={{ background: '#ede9fb', color: '#6d5ce8' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>search</span>
                {summaryLoading ? '...' : 'Detect'}
              </button>
            </div>
            {patterns ? <SummaryRenderer text={patterns} /> : (
              <p className="text-sm text-gray-400 text-center py-2">Tap "Detect" to find health patterns</p>
            )}
          </div>

          <div className="rounded-2xl p-4 flex gap-3 mt-1 mb-3" style={{ background: '#fdf3e0', border: '1px solid #f5e3bd' }}>
            <span className="material-symbols-outlined shrink-0 mt-0.5" style={{ fontSize: '18px', color: '#e08a1f', fontVariationSettings: '"FILL" 1' }}>warning</span>
            <p className="text-sm text-[#5c4a1f] leading-relaxed">
              <span className="font-bold" style={{ color: '#c9791a' }}>Disclaimer:</span> This summary is generated based only on the data you have uploaded. This is <span className="font-bold">not medical advice or a prescription</span>. Please consult your doctor for any health decisions.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default AITab;